/*
 * PRONOUNCE — the microphone control and the ONE practice popup behind it.
 * docs/project/ai-features-plan.md §1, decisions 1–5 and the 2026-08-20 TWO-RING VERDICT review.
 *
 * WHY ONE POPUP AND NOT ONE PER SURFACE
 * A microphone stands beside every speaker in the app — the rich head, a lean dictionary row, a
 * tray row, Learn's reveal strip, the drill's revealed head and sound-only cue, a collocation and
 * an example gutter. Every one of them opens THIS surface, moved to a new anchor. Practice is one
 * thing wherever it starts, so it looks and behaves like one thing, and there is exactly one place
 * where the hold-to-talk contract, the Space routing and the score painting are written down.
 *
 * THE FOUR RULES THAT LOOK LIKE DETAILS AND ARE NOT
 * 1. The MIC IS A CLICK, not a hold. Holding happens on the big microphone INSIDE the popup. A
 *    hold on a 32px row icon would start recording before the learner could see what they were
 *    being asked to say.
 * 2. There are TWO judgments, not one, and the surface says both (owner review 2026-08-20).
 *    RECOGNITION — would a listener recover your words — and ACCENT — how close the sound is to
 *    native. They diverge constantly: a thick accent can be perfectly intelligible, and muddy
 *    near-native sound can be unintelligible. The old single ring quietly meant "weakest sound",
 *    which borrowed the universal "overall success" idiom to say something else.
 * 3. A RED DIAL ALWAYS NAMES ITS CULPRIT. The screenshot that triggered the review was a red ring,
 *    three green numbers and not one painted letter. So recognition-red prints what was actually
 *    heard and which word never arrived, and accent-red ALWAYS paints the chunk holding the
 *    weakest sound — even when per-grapheme aggregation alone would leave every letter plain,
 *    which is the grain seam that produced that screenshot. Since the average ear went live
 *    (2026-08-20) "what was heard" prefers ITS transcript over the script-primed one, and the
 *    middle band — understood by a patient listener, not by an average one — says so in a line.
 * 4. The rings are QUANTIZED — five steps for a word or a collocation, ten for a sentence. A
 *    single-word score carries ±5–10 of noise; a sentence is steadier and earns the finer scale.
 *
 * WHAT IS SESSION-ONLY AND WHAT IS NOT
 * The verdict is a row in the database: both dials fill from it on reopen, on any device. The
 * learner's own RECORDING is a Blob in this file's memory and nothing else — never uploaded,
 * never stored, dropped when the tab closes. THE DIALS ARE THE PLAYBACK CONTROLS (outer ring =
 * this attempt, inner disc = your best), so a reopened popup has filled dials with no audio behind
 * them: clicking one then says so in a line rather than doing nothing, because silence on a filled
 * control reads as a broken app.
 */
window.PRONOUNCE = (function () {
	"use strict";

	/* Dial geometry — ONE viewBox for both dials, sized by CSS. r=17 in a 44-box gives a 106.8
	   circumference, which is the only magic number here: every dash length is a fraction of it. */
	var DIAL_CIRC = 106.8;

	/* Steps per rung (owner, third round 2026-08-20). The measured ±5–10 single-word noise keeps
	   the short rungs coarse; only a sentence is steady enough to carry ten. */
	var STEPS_SHORT = 5, STEPS_SENTENCE = 10;

	/* The two thresholds the whole readout turns on (owner review 2026-08-20 — flagged as MINE):
	   at or above GOOD a sound is left as plain ink, between GOOD and FAIR it wears the amber wash,
	   below FAIR the red one. They are the answer-diff family's own three bands, which the learner
	   already reads on every typed answer. */
	var GOOD = 80, FAIR = 60;

	var popupEl = null;      // the surface, built once and reused
	var layer = null;        // its PL.layers handle
	var anchorEl = null;     // the mic that opened it — wears the pressed state while open
	var current = null;      // the resolved spec of what is being practiced
	var recorder = null;
	var recording = false;
	var busy = false;        // a clip is in flight — a second hold must not start one

	/**
	 * Playback of the learner's own attempts, keyed BY ATTEMPT ID — the same key the two dials
	 * point at, so the outer ring and the inner disc can hold two different recordings of the same
	 * word. Session memory: gone on reload, by design.
	 */
	var myAudio = {};

	/** refKey → the attempt keys that reference still wants kept. Everything else is released. */
	var retained = {};

	/** The clip captured by the microphone, waiting for the server to say which attempt it is. */
	var pendingClip = null;

	// ── small helpers ────────────────────────────────────────────────────────────────────────

	function res(key) { return typeof getRes === "function" ? getRes(key) : key; }

	function esc(s) {
		return String(s == null ? "" : s)
			.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	/** The chosen English, the one setting that decides voice, transcription and judging model. */
	function dialect() { return window.en_dialect === "UK" ? "UK" : "US"; }

	function keyOf(spec) {
		return spec.termId != null
			? "t" + spec.termId + ":" + dialect()
			: "p" + spec.phraseKind + ":" + spec.phraseRefId + ":" + dialect();
	}

	/** Sentences earn the finer ring; a word and a collocation do not (rule 4). */
	function rungSteps(spec) {
		return spec && spec.phraseKind
			&& String(spec.phraseKind).toUpperCase() === "EXAMPLE" ? STEPS_SENTENCE : STEPS_SHORT;
	}

	/** Strip whatever delimiters the corpus stored, then add exactly one pair. */
	function ipaText(raw) {
		var s = String(raw || "").replace(/[\/\[\]]/g, " ").replace(/\s+/g, " ").trim();
		return s ? "/" + s + "/" : "";
	}

	/**
	 * A sentence set at headline size is three lines tall and outweighs everything under it — and
	 * what has to carry this screen is the DIALS. Past this length the reference steps down a size
	 * instead of pushing them off the bottom. Measured against the corpus's own shapes: a German
	 * compound noun and a short collocation stay big, a full example sentence does not.
	 */
	function isLongRef(text) { return String(text || "").length > 18; }

	function band(score) {
		if (score == null) { return null; }
		return score >= GOOD ? "good" : (score >= FAIR ? "fair" : "weak");
	}

	// ── the dials ────────────────────────────────────────────────────────────────────────────

	/**
	 * One dial — the load-bearing surface of this popup, and both halves of it are live.
	 *
	 * <p>OUTER stepped ring = the LAST attempt, quantized (rule 4). A score of 0 still shows ONE
	 * step rather than an empty track: an empty ring reads as "nothing measured", and something was
	 * measured, badly. INNER band-coloured disc = the personal BEST, with no steps at all — a
	 * quantized arc is unreadable at that size, so the disc carries a band and the tooltip carries
	 * the number.
	 *
	 * <p>WHITE OUTLINE ONLY is the one empty state: no attempt exists for this reference in this
	 * dialect. It is deliberately the only unfilled thing on the surface.
	 */
	function dialHtml(d) {
		var steps = d.last == null ? 0
			: Math.max(1, Math.min(d.steps, Math.round(d.last * d.steps / 100)));
		var dash = (DIAL_CIRC * steps / d.steps).toFixed(1);
		var empty = d.last == null && d.best == null;
		var svg = '<svg class="pronDialClass' + (empty ? " pronDialEmptyClass" : "")
			+ '" viewBox="0 0 44 44" role="img" aria-label="' + esc(d.label) + '">'
			+ '<circle class="pronDialTrackClass" cx="22" cy="22" r="17" fill="none"></circle>';
		if (steps) {
			svg += '<circle class="pronDialArcClass pronDialArc-' + band(d.last) + '"'
				+ ' cx="22" cy="22" r="17" fill="none" stroke-linecap="round"'
				+ ' stroke-dasharray="' + dash + ' ' + DIAL_CIRC + '"'
				+ ' transform="rotate(-90 22 22)"></circle>';
		}
		svg += '<circle class="pronDialDiscClass '
			+ (d.best == null ? "pronDialDiscEmptyClass" : "pronDialDisc-" + band(d.best))
			+ '" cx="22" cy="22" r="10.5"></circle>'
			+ hitCircle("pronDialOuterHitClass", d.last != null, d.lastKey,
				'cx="22" cy="22" r="17" stroke-width="7"', d.lastTip)
			+ hitCircle("pronDialInnerHitClass", d.best != null, d.bestKey,
				'cx="22" cy="22" r="9.6"', d.bestTip)
			/* AFTER the hit circles on purpose: the hover affordance is a sibling selector away
			   (~) from the element that is actually hovered. */
			+ '<g class="pronDialPlayClass pronDialPlayOuterClass">'
			+   '<circle cx="22" cy="5" r="4.2"></circle>'
			+   '<path d="M20.6 3 L23.8 5 L20.6 7 Z"></path></g>'
			+ '<g class="pronDialPlayClass pronDialPlayInnerClass">'
			+   '<path d="M19.9 18.7 L25.3 22 L19.9 25.3 Z"></path></g>'
			+ '</svg>';
		return '<div class="pronDialBoxClass">' + svg
			+ '<span class="pronDialLabelClass">' + esc(d.label) + '</span></div>';
	}

	/**
	 * The invisible half of a dial: what a click and a hover actually land on. Filled means "there
	 * is an attempt behind this" — which is what makes it clickable, whether or not the recording
	 * is still in memory. Playable means the blob IS still here, and is the ONLY thing that earns
	 * the little play triangle.
	 */
	function hitCircle(cls, filled, audioKey, geom, tip) {
		return '<circle class="' + cls + (filled ? " pronDialFilledClass" : "")
			+ (filled && myAudio[audioKey] ? " pronCanPlayClass" : "") + '" ' + geom
			+ (tip ? ' data-pron-tip="' + esc(tip) + '"' : "") + '></circle>';
	}

	/** A dial, with its two halves wired to the two recordings they stand for. */
	function buildDial(d) {
		var el = $(dialHtml(d));
		el.find(".pronDialOuterHitClass").on("click", function () { playHeld(d.lastKey); });
		el.find(".pronDialInnerHitClass").on("click", function () { playHeld(d.bestKey); });
		return el;
	}

	/**
	 * @param emptyKey what this half says when it has no number — and the two empties are NOT the
	 *                 same news: nothing said here at all, versus said but no best of this kind
	 *                 yet (which is what the rows written before the recognition column look like).
	 */
	function dialTip(labelKey, score, audioKey, emptyKey) {
		if (score == null) { return res(emptyKey || "pronDialNone_tooltip"); }
		var out = strFormat(res(labelKey), Math.round(score));
		if (myAudio[audioKey]) { out += "\n" + res("pronDialPlay_hint"); }
		return out;
	}

	// ── the learner's own voice ──────────────────────────────────────────────────────────────

	/** The key an attempt's recording is filed under. A row that could not be stored has no id. */
	function audioKey(spec, attempt) {
		if (!attempt) { return null; }
		return attempt.id != null ? "a" + attempt.id : "x" + keyOf(spec);
	}

	function playHeld(key) {
		if (!key) { return; }            // an empty dial half — nothing was ever said here
		var held = myAudio[key];
		if (held) {
			held.audio.currentTime = 0;
			held.audio.play();
			return;
		}
		/* Filled, but the voice is gone. What happened and what the constraint is, then stop. */
		popupEl.find(".pronHintClass").removeClass("pronHintWarnClass").addClass("pronHintNoteClass")
			.text(res("pronNoRecording_message")).css("display", "block");
		position();
	}

	/** The ORIGINAL recording, filed under the attempt the server just wrote. Playback only. */
	function bindAudio(spec, attempt) {
		var blob = pendingClip;
		pendingClip = null;
		var key = audioKey(spec, attempt);
		if (!blob || !key) { return; }
		if (myAudio[key]) { URL.revokeObjectURL(myAudio[key].url); }
		var url = URL.createObjectURL(blob);
		myAudio[key] = { url: url, audio: new Audio(url) };
	}

	/**
	 * Keep the recordings the dials can still point at — the last and the two bests of every
	 * reference opened in this tab — and release the rest. Blobs are the one thing on this surface
	 * that costs memory, so nothing unreachable is held.
	 */
	function retain(spec, keys) {
		retained[keyOf(spec)] = keys;
		var keep = {}, k, i;
		for (k in retained) {
			if (!retained.hasOwnProperty(k)) { continue; }
			for (i = 0; i < retained[k].length; i++) {
				if (retained[k][i]) { keep[retained[k][i]] = true; }
			}
		}
		for (k in myAudio) {
			if (myAudio.hasOwnProperty(k) && !keep[k]) {
				URL.revokeObjectURL(myAudio[k].url);
				delete myAudio[k];
			}
		}
	}

	// ── painting the word ────────────────────────────────────────────────────────────────────

	/** The letters of a string, lowercased — what a grapheme run has to line up with. */
	function letters(s) { return String(s || "").toLowerCase(); }

	function isOmission(w) {
		return w && w.errorType && w.errorType.toLowerCase() === "omission";
	}

	function isInsertion(w) {
		return w && w.errorType && w.errorType.toLowerCase() === "insertion";
	}

	/**
	 * The words that stand for the REFERENCE, in reference order. Insertions are dropped, because
	 * they are words the learner said that were never in the script — leaving them in shifts every
	 * later word onto the wrong token, which is exactly what happened on the stored "ein
	 * zufriedener Kunde" attempt (heard as "Kunde zufriedener": Azure returns the extra "Kunde"
	 * FIRST, so the whole phrase painted one word out of step).
	 */
	function referenceWords(attempt) {
		var all = (attempt && attempt.words) || [];
		var out = [];
		for (var i = 0; i < all.length; i++) {
			if (!isInsertion(all[i])) { out.push(all[i]); }
		}
		return out;
	}

	/**
	 * One word repainted on its LETTERS, using Azure's per-syllable grapheme mapping — which comes
	 * back in EVERY locale measured, including German, where phoneme names do not. That is what
	 * makes this legible to a learner who has never read IPA.
	 *
	 * <p>The alignment is checked, not assumed: the graphemes are concatenated and compared to the
	 * word's own letters. When they do not agree the whole word is washed instead. Painting a
	 * mis-aligned run would put the red on the wrong syllable, which is worse than not painting.
	 *
	 * @param force the grain-seam fix — the chunk that MUST be painted because it holds the
	 *              utterance's weakest sound, at the band that sound earns, whatever the syllable's
	 *              own aggregate says.
	 */
	function paintWordLetters(text, w, force) {
		var syl = w && w.syllables;
		if (!syl || !syl.length) { return paintWholeWord(text, w, force); }
		var joined = "";
		var i;
		for (i = 0; i < syl.length; i++) { joined += letters(syl[i].grapheme); }
		if (joined !== letters(text)) { return paintWholeWord(text, w, force); }

		var html = "", at = 0;
		for (i = 0; i < syl.length; i++) {
			var n = String(syl[i].grapheme || "").length;
			var piece = text.substr(at, n);
			at += n;
			html += chunk(piece, forced(syl[i].accuracy, force && force.syllable === i ? force : null),
				syl[i].grapheme, w);
		}
		return html;
	}

	/** No usable syllable data: the word is one chunk, washed on its own score. */
	function paintWholeWord(text, w, force) {
		return chunk(text, forced(w ? w.accuracy : null, force), text, w);
	}

	/**
	 * The forced score, and the guard that makes forcing safe: it may only ever pull a chunk DOWN.
	 * The fix exists because aggregation hides a bad sound, not because it invents one — a syllable
	 * that already scored 55 keeps its 55 rather than being lifted to the word's 70.
	 */
	function forced(own, force) {
		if (!force || force.score == null) { return own; }
		return own == null || force.score < own ? force.score : own;
	}

	/**
	 * One painted run. GOOD stays plain ink and carries no tooltip at all — decision 3 says weak
	 * chunks only, and a tooltip on every letter of a word said well is noise wearing a hat.
	 */
	function chunk(text, score, label, w) {
		var b = band(score);
		if (!b || b === "good") { return esc(text); }
		return '<span class="pronChunkClass pronChunk-' + b + '" data-pron-tip="'
			+ esc(chunkTip(label, score, w)) + '">' + esc(text) + '</span>';
	}

	/**
	 * What a weak chunk says when you hover it: the sound and its score, plus — where the provider
	 * named the phonemes, which is en-US only — what it sounded like instead. That last line is the
	 * whole per-sound feedback loop in one sentence, and it simply does not exist for en-GB or
	 * de-DE, where the letters and the number carry it alone.
	 */
	function chunkTip(label, score, w) {
		var out = label + " · " + Math.round(score);
		var worst = worstPhoneme(w);
		var alt = worst && worst.phoneme && worst.accuracy < GOOD ? firstAlt(worst) : "";
		if (alt) { out += "\n" + strFormat(res("pronSoundedLike_tip"), alt, worst.phoneme); }
		return out;
	}

	function worstPhoneme(w) {
		if (!w || !w.phonemes) { return null; }
		var worst = null;
		for (var i = 0; i < w.phonemes.length; i++) {
			var p = w.phonemes[i];
			if (!p || p.accuracy == null) { continue; }
			if (!worst || p.accuracy < worst.accuracy) { worst = p; }
		}
		return worst;
	}

	/** The first thing the provider says it sounded like instead — en-US only, blank elsewhere. */
	function firstAlt(p) {
		var list = (p && p.soundedLike) || [];
		for (var i = 0; i < list.length; i++) {
			if (list[i] && list[i] !== p.phoneme) { return list[i]; }
		}
		return "";
	}

	function worstSyllable(w) {
		var syl = (w && w.syllables) || [];
		var at = null;
		for (var i = 0; i < syl.length; i++) {
			if (syl[i] && syl[i].accuracy != null && (at === null || syl[i].accuracy < syl[at].accuracy)) {
				at = i;
			}
		}
		return at;
	}

	/**
	 * The weakest sound in the utterance and the chunk that holds it — the diagnosis layer's whole
	 * subject, and the thing accent-red must paint.
	 *
	 * <p>Omitted and inserted words are skipped: a word that never arrived is the RECOGNITION
	 * dial's news, and calling it the weakest accent would say the same thing twice.
	 *
	 * <p>The chunk is the weakest SYLLABLE of the weakest word rather than a phoneme-to-syllable
	 * mapping the provider does not hand over. On every measured attempt they are the same
	 * syllable, and where they are not, the syllable actually scored lowest is the honest one to
	 * paint.
	 */
	function weakSpot(attempt) {
		var words = referenceWords(attempt);
		var spot = null;
		for (var i = 0; i < words.length; i++) {
			var w = words[i];
			if (!w || isOmission(w)) { continue; }
			var p = worstPhoneme(w);
			var sylAt = worstSyllable(w);
			// The finest grain the provider actually gave for this word: a named phoneme where it
			// names them, the weakest syllable where it does not, the word itself as a last resort.
			var score = p ? p.accuracy : (sylAt !== null ? w.syllables[sylAt].accuracy : w.accuracy);
			if (score == null) { continue; }
			if (!spot || score < spot.score) {
				spot = {
					wordAt: i, word: w, score: score,
					phoneme: p && p.phoneme ? p.phoneme : "",
					alt: p ? firstAlt(p) : "",
					syllable: sylAt
				};
			}
		}
		return spot && spot.score < GOOD ? spot : null;
	}

	/**
	 * The grain-seam fix, and the reason it is gated. A GREEN accent dial with red letters under it
	 * would contradict itself; an amber or red one with nothing painted is the screenshot that
	 * caused this redesign. So the forced paint appears exactly when the dial is not green.
	 */
	function forcedSpot(attempt) {
		if (!attempt || attempt.accuracy == null || band(attempt.accuracy) === "good") { return null; }
		return weakSpot(attempt);
	}

	/**
	 * The reference, repainted.
	 *
	 * <p>Two grains, decided by what is being practiced rather than by counting words: a TERM is
	 * painted on its letters (decision 3), a PHRASE on its words (decision 6 of the popup spec) —
	 * a sentence painted letter by letter is a ransom note, and the useful question there is which
	 * WORD went wrong. It is also the only grain a sentence HAS: measured, sentence attempts come
	 * back with flat per-word phoneme scores.
	 */
	function paintReference(spec, attempt) {
		var words = referenceWords(attempt);
		var refText = (attempt && attempt.referenceText) || spec.spokenText || spec.text || "";
		if (!words.length) { return esc(refText); }

		var spot = forcedSpot(attempt);
		var tokens = refText.split(/(\s+)/);   // keep the separators so spacing survives
		var wordAt = 0, html = "";
		var perLetter = spec.termId != null;
		for (var i = 0; i < tokens.length; i++) {
			if (!tokens[i] || /^\s+$/.test(tokens[i])) { html += esc(tokens[i]); continue; }
			var at = wordAt++;
			var w = words[at];
			if (!w) { html += esc(tokens[i]); continue; }
			var force = spot && spot.wordAt === at ? spot : null;
			if (isOmission(w)) {
				html += '<span class="pronChunkClass pronChunk-omitted" data-pron-tip="'
					+ esc(res("pronOmitted_tip")) + '">' + esc(tokens[i]) + '</span>';
			} else if (perLetter) {
				html += paintWordLetters(tokens[i], w, force);
			} else {
				html += paintWholeWord(tokens[i], w, force);
			}
		}
		return html;
	}

	// ── the diagnosis layer ──────────────────────────────────────────────────────────────────

	/** Words, lower-cased, punctuation gone — enough to tell "Water." from "water" apart. */
	function normalWords(text) {
		return String(text == null ? "" : text).toLowerCase()
			.replace(/[^\p{L}\p{N}]+/gu, " ").trim().split(/\s+/).filter(function (t) { return !!t; });
	}

	function leadsWithArticle(refText, article) {
		var words = normalWords(refText);
		return words.length > 1 && words[0] === String(article).toLowerCase().trim();
	}

	/** The reference words the provider says never arrived — its own verdict, not a re-derivation. */
	function omittedWords(attempt) {
		var words = referenceWords(attempt);
		var out = [];
		for (var i = 0; i < words.length; i++) {
			if (isOmission(words[i]) && words[i].word) { out.push(words[i].word); }
		}
		return out;
	}

	/**
	 * A red dial never stands alone (rule 3). Recognition first — being understood is the question
	 * that comes before sounding native — then the weakest sound, then the demoted dimensions,
	 * which appear ONLY when they explain something the learner can hear.
	 */
	function paintDiagnosis(spec, attempt) {
		var box = popupEl.find(".pronDiagClass").empty();
		if (!attempt) { box.css("display", "none"); return; }
		var lines = [];
		recognitionLines(lines, attempt);
		accentLine(lines, attempt);
		/* Nothing was heard at all: "long pauses between the words" is true of silence and tells
		   the learner nothing they do not already know from the line above it. */
		if (normalWords(attempt.recognizedText).length) { explanationLines(lines, attempt); }
		for (var i = 0; i < lines.length; i++) {
			box.append($('<span class="pronDiagLineClass"></span>')
				.addClass(lines[i].soft ? "pronDiagSoftClass" : "")
				.text(lines[i].text));
		}
		box.css("display", lines.length ? "flex" : "none");
	}

	/**
	 * What the AVERAGE ear wrote down, when it is worth printing.
	 *
	 * <p>Two ears judge every attempt (docs/project/intelligibility-ear-research.md): the charitable
	 * one was handed the script and rounds a near-miss UP to it — "Hunde" came back as "Kunde." —
	 * while this one never knew what was coming. So when they disagree, THIS is the transcript that
	 * explains the verdict, and the primed one would quietly contradict the dial.
	 *
	 * @return "" unless there is a BAND — a transcript with no band was refused by a hardening gate
	 *         (a decoder loop, a fabrication from silence) and is the decoder's failure, not
	 *         something the learner said, so it is never shown — and unless it actually diverges
	 *         from the reference, since "you said what you were asked to" is not news.
	 */
	function earHeard(a) {
		if (!a.recognitionBand || !a.earTranscript) { return ""; }
		var heard = String(a.earTranscript).trim();
		if (!normalWords(heard).length) { return ""; }
		return normalWords(heard).join(" ") === normalWords(a.referenceText).join(" ") ? "" : heard;
	}

	/**
	 * What was actually heard, which word never made it — and, in the middle band, WHICH LISTENER
	 * got you. The middle band is the productive zone of the two-ear score ("a patient listener
	 * understands you; an average listener struggles"), and it is wide on purpose: without that one
	 * line an amber dial between two green ones would look like a bug.
	 */
	function recognitionLines(lines, a) {
		if (a.recognition == null || a.recognition >= GOOD) { return; }
		var heard = String(a.recognizedText == null ? "" : a.recognizedText).trim();
		if (!normalWords(heard).length) {
			lines.push({ text: res("pronDiagSilence_text") });
			return;
		}
		var missing = omittedWords(a);
		if (missing.length) {
			lines.push({ text: strFormat(res("pronDiagMissed_text"), missing.join(", ")) });
		}
		var ear = earHeard(a);
		if (ear) {
			lines.push({ text: strFormat(res("pronDiagEarHeard_text"), ear) });
		} else if (normalWords(heard).join(" ") !== normalWords(a.referenceText).join(" ")) {
			lines.push({ text: strFormat(res("pronDiagHeard_text"), heard) });
		}
		if (a.recognitionBand === "MIDDLE") {
			lines.push({ text: res("pronDiagPatient_text") });
		}
	}

	/** The weakest sound, named — the half of the old headline that belongs down here. */
	function accentLine(lines, a) {
		var spot = forcedSpot(a);
		if (!spot) { return; }
		var many = normalWords(a.referenceText).length > 1;
		var word = spot.word.word || "";
		var line;
		if (spot.phoneme) {
			line = many ? strFormat(res("pronDiagSound_text"), spot.phoneme, word)
				: strFormat(res("pronDiagSoundOnly_text"), spot.phoneme);
			if (spot.alt) {
				line += " — " + strFormat(res("pronSoundedLike_tip"), spot.alt, spot.phoneme);
			}
		} else {
			var g = spot.syllable !== null && spot.word.syllables
				? spot.word.syllables[spot.syllable].grapheme : null;
			if (g) {
				line = many ? strFormat(res("pronDiagPart_text"), g, word)
					: strFormat(res("pronDiagPartOnly_text"), g);
			} else if (many) {
				line = strFormat(res("pronDiagWord_text"), word);
			} else {
				return;   // nothing nameable: the wash on the word is the whole message
			}
		}
		lines.push({ text: line });
	}

	/**
	 * Fluency, completeness and prosody, DEMOTED (owner review 2026-08-20): Azure's dimension names
	 * translated literally — "полнота 100" — are provider telemetry on a learner's screen and teach
	 * nobody anything. What survives is the sentence each number can justify, and only when it is
	 * far enough off to be something the learner would recognise in their own recording.
	 */
	function explanationLines(lines, a) {
		if (a.completeness != null && a.completeness < GOOD && !omittedWords(a).length) {
			lines.push({ text: res("pronExpPartial_text"), soft: true });
		}
		if (a.fluency != null && a.fluency < FAIR) {
			lines.push({ text: res("pronExpPauses_text"), soft: true });
		}
		if (a.prosody != null && a.prosody < FAIR) {
			lines.push({ text: res("pronExpFlat_text"), soft: true });
		}
	}

	// ── the surface ──────────────────────────────────────────────────────────────────────────

	function build() {
		if (popupEl) { return popupEl; }
		popupEl = $(
			'<div class="pronPopClass" role="dialog">'
			+ '<button type="button" class="pronCloseClass pl-btn-icon-round">'
			+   '<span class="pl-ic pl-ic-x"></span></button>'
			+ '<div class="pronHeadClass">'
			+   '<span class="pronArticleClass"></span>'
			+   '<span class="pronWordClass"></span>'
			+ '</div>'
			+ '<div class="pronIpaClass"></div>'
			+ '<div class="pronDialsClass"></div>'
			+ '<div class="pronLegendClass"></div>'
			+ '<div class="pronDiagClass"></div>'
			+ '<div class="pronControlsClass">'
			+   '<button type="button" class="pronMicBigClass">'
			+     '<span class="pl-ic pl-ic-mic"></span></button>'
			+   '<button type="button" class="pronSayClass">'
			+     '<span class="pl-ic pl-ic-volume"></span></button>'
			+ '</div>'
			+ '<div class="pronHintClass"></div>'
			+ '</div>');
		$("body").append(popupEl);

		popupEl.find(".pronCloseClass").assignTooltip("pronClose_tooltip")
			.on("click", function () { close(); });
		popupEl.find(".pronSayClass").assignTooltip("sound_tooltip")
			.on("click", function () { playReference(); });

		/* Hold to talk. pointerdown starts; the RELEASE is listened for on the document, because a
		   learner who drifts off the button while speaking has still finished speaking — losing
		   their clip because the cursor moved four pixels would be the single most annoying bug
		   this feature could have. */
		var mic = popupEl.find(".pronMicBigClass");
		mic.assignTooltip("pronHold_tooltip");
		mic[0].addEventListener("pointerdown", function (e) {
			e.preventDefault();   // no text selection, no focus ring stealing the release
			startRecording();
		});

		/* The tooltip engine renders our chunk tips AND both halves of both dials: one delegate for
		   every tipped node the popup will ever draw, the charts' own pattern. Attached once with
		   the popup — and it matches SVG nodes too, which is what lets a ring carry its own text. */
		if (window.PL && PL.tip) {
			PL.tip.delegate(popupEl[0], "[data-pron-tip]", {
				guided: false,
				content: function (el) {
					return esc(el.getAttribute("data-pron-tip")).replace(/\n/g, "<p/>");
				}
			});
		}
		return popupEl;
	}

	// ── open / close ─────────────────────────────────────────────────────────────────────────

	function resolveSpec(spec) {
		var s = typeof spec === "function" ? spec() : spec;
		return s || null;
	}

	function open(anchor, spec) {
		var s = resolveSpec(spec);
		if (!s) { return; }
		if (layer && anchorEl === anchor) { close(); return; }   // the toggle half of a toggle
		close();

		current = s;
		anchorEl = anchor;
		var el = build();
		el.toggleClass("pronPopLgClass", !!s.large);
		el.removeClass("pronHasResultClass");
		paintIdle(s);
		// A CLASS, not .show(): jQuery 1.9 would write display:block over a flex column.
		el.addClass("pronOpenClass");

		$(anchor).addClass("pl-btn-toggle-active").attr("aria-pressed", "true");
		position();

		layer = PL.layers.push({
			el: el[0],
			anchors: [anchor],   // the opener is never an "outside" press, or the click would reopen
			onClose: function () { teardown(); }
		});
		armSpace();
		warmMic();
		loadState(s);
	}

	/* The microphone opens NOW, while the learner is still reading the word — not on the first
	   hold, where the device wake-up (driver init + the AGC ramp) repeatedly clipped the first
	   attempt into "nothing was heard". The stream then stays warm across holds and is released
	   in teardown, which is also when the browser's recording indicator goes out. */
	function warmMic() {
		if (!window.pronounceRecorder || !pronounceRecorder.isSupported()) { return; }
		if (!recorder) { recorder = pronounceRecorder.create(); }
		recorder.open().catch(function () {
			// Refused or absent: nothing to say yet — the first hold explains it, in words.
		});
	}

	function close() {
		if (layer) { layer.close("api"); return; }   // funnels back here through onClose
		teardown();
	}

	function teardown() {
		layer = null;
		disarmSpace();
		stopRecording(true);
		if (recorder) { recorder.release(); recorder = null; }   // indicator out with the popup
		if (anchorEl) {
			$(anchorEl).removeClass("pl-btn-toggle-active").attr("aria-pressed", "false");
			anchorEl = null;
		}
		if (popupEl) { popupEl.removeClass("pronOpenClass pronRecordingClass"); }
		current = null;
	}

	function position() {
		if (!popupEl || !anchorEl || !window.PL || !PL.tip) { return; }
		PL.tip.place({ el: anchorEl }, popupEl[0], {
			sides: ["below", "above", "right", "left"], gap: 8, margin: 10
		});
	}

	// ── Space, while the popup is open ───────────────────────────────────────────────────────

	/*
	 * Space is the app's sound key everywhere, and while this popup is up the sound it means is
	 * THIS word. The handler is a CAPTURE-phase listener for the same reason PL.layers uses one:
	 * it runs before every bubble handler in the app, so the Dictionary's document handler, Learn's
	 * and the drill's all stand down without knowing this popup exists. stopImmediatePropagation
	 * is what makes that a stand-down rather than a race, and preventDefault is what stops a
	 * FOCUSED BUTTON inside the popup from also firing — Space activates a button, so without it
	 * the close × would take a press aimed at the speaker.
	 */
	var spaceArmed = false;

	function onSpaceCapture(e) {
		if (e.key !== " " && e.keyCode !== 32) { return; }
		if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey || e.isComposing) { return; }
		var t = e.target || {};
		var tag = (t.tagName || "").toLowerCase();
		// A caret always beats the shortcut — the drill pane's own rule, and it has to hold here
		// too or a learner typing in a field behind the popup would lose their spaces.
		if (t.isContentEditable || tag === "input" || tag === "textarea" || tag === "select") { return; }
		e.preventDefault();
		e.stopImmediatePropagation();
		if (!e.repeat) { playReference(); }
	}

	function armSpace() {
		if (spaceArmed) { return; }
		document.addEventListener("keydown", onSpaceCapture, true);
		spaceArmed = true;
	}

	function disarmSpace() {
		if (!spaceArmed) { return; }
		document.removeEventListener("keydown", onSpaceCapture, true);
		spaceArmed = false;
	}

	// ── painting states ──────────────────────────────────────────────────────────────────────

	function paintIdle(s) {
		var el = popupEl;
		el.find(".pronArticleClass").text(s.article || "").css("display", s.article ? "inline" : "none");
		el.find(".pronWordClass").text(s.text || "");
		var ipa = ipaText(s.ipa);
		el.find(".pronIpaClass").text(ipa).css("display", ipa ? "block" : "none");
		el.toggleClass("pronLongRefClass", isLongRef(s.text));
		el.find(".pronDialsClass").empty();     // CSS hides it while it is :empty
		el.find(".pronLegendClass").empty();
		el.find(".pronDiagClass").empty().css("display", "none");
		el.find(".pronHintClass").removeClass("pronHintWarnClass pronHintNoteClass")
			.text(res("pronHint_hold")).css("display", "block");
	}

	/** LAST + BOTH BESTS from the database — what makes a reopen identical anywhere. */
	function loadState(s) {
		$.ajax({ url: "api/pronounce/state", type: "GET", dataType: "json", data: refParams(s) })
			.done(function (r) {
				if (current !== s) { return; }   // the learner moved on while this was in flight
				applyWarmup(r && r.warmupSecondsLeft);
				if (r && r.ok) { paintVerdict(s, stateOf(r)); }
				else if (r && !r.available) { paintRefusal("UNAVAILABLE", r); }
			});
	}

	var warmupTimer = null;

	/**
	 * The microphone's warm-up (docs/project/go-public-hardening.md §8.6): a browser that arrived
	 * seconds ago does not get one, because no human reaches the mic that fast and the server will
	 * refuse anyway. Only a brand-new GUEST ever sees this; everybody else gets 0 back and nothing
	 * happens here.
	 *
	 * <p><b>No message, deliberately.</b> "The microphone unlocks in 43 seconds" is a rule nobody
	 * asked about and an invitation to wonder what else is being withheld; a control that is not
	 * there yet and then is reads as a page finishing loading. The timer reveals it in place.
	 */
	function applyWarmup(secondsLeft) {
		if (warmupTimer) { clearTimeout(warmupTimer); warmupTimer = null; }
		var left = Number(secondsLeft) || 0;
		var mic = popupEl && popupEl.find(".pronMicBigClass");
		if (!mic || !mic.length) { return; }
		if (left <= 0) { mic.css("display", ""); return; }
		mic.css("display", "none");
		warmupTimer = setTimeout(function () {
			warmupTimer = null;
			if (popupEl) { popupEl.find(".pronMicBigClass").css("display", ""); }
		}, left * 1000);
	}

	/**
	 * The server's answer as the two dials read it. {@code best} keeps the short name it has always
	 * had on the wire and now means the best BY ACCENT; {@code bestRecognition} is the other dial's
	 * disc, and is a different attempt more often than not. {@code nativeDial} false = this
	 * language has no Native-like judge here, so that dial is not drawn at all.
	 */
	function stateOf(r) {
		return { last: r.last, bestAccent: r.best, bestRecognition: r.bestRecognition,
			nativeDial: r.nativeDial };
	}

	function refParams(s) {
		return s.termId != null
			? { termId: s.termId }
			: { phraseKind: s.phraseKind, phraseRefId: s.phraseRefId };
	}

	/**
	 * The verdict on screen: the word repainted on its weak parts, the TWO dials, and — under a red
	 * one — the line that names its culprit.
	 *
	 * <p>Called on every open, including when nothing has ever been said here: two white
	 * outline-only dials are the empty state, and they are drawn rather than withheld so the
	 * learner can see what is about to be filled in.
	 */
	function paintVerdict(spec, state) {
		var el = popupEl;
		var last = state && state.last;
		var bestAcc = state && state.bestAccent;
		var bestRec = state && state.bestRecognition;
		el.toggleClass("pronHasResultClass", !!last);
		el.toggleClass("pronLongRefClass",
			isLongRef(last ? last.referenceText : spec.text));
		el.find(".pronWordClass").html(last ? paintReference(spec, last) : esc(spec.text || ""));
		/* The painted reference is the SPOKEN text, which on a German noun already carries the
		   article the head shows separately — "die die Abartigkeit" otherwise. The article span
		   stands down for exactly that overlap and comes back for the idle state. */
		el.find(".pronArticleClass").css("display",
			spec.article && !(last && leadsWithArticle(last.referenceText, spec.article))
				? "inline" : "none");

		var lastKey = audioKey(spec, last);
		var accKey = audioKey(spec, bestAcc);
		var recKey = audioKey(spec, bestRec);
		retain(spec, [lastKey, accKey, recKey]);

		/* The Native-like dial's value: the language-confidence judge, or NOTHING. Azure's
		   accuracy never fills in — it was measured INVERTED on this axis (rewards a deliberate
		   fake accent; research note B6), and the one evening it served as the fallback it put
		   unbelievable full-green dials on attempts whose judge had merely timed out. An empty
		   dial is the honest reading for "this axis was not measured" — old rows included. */
		function accentScore(a) {
			return a && a.nativeScore != null ? a.nativeScore : null;
		}

		var steps = rungSteps(spec);
		var dials = el.find(".pronDialsClass").empty();
		dials.append(buildDial({
			label: res("pronDialRecognition_label"), steps: steps,
			last: last ? last.recognition : null, best: bestRec ? bestRec.recognition : null,
			lastKey: last ? lastKey : null, bestKey: bestRec ? recKey : null,
			lastTip: dialTip("pronDialRecLast_tooltip", last ? last.recognition : null, lastKey),
			bestTip: dialTip("pronDialRecBest_tooltip", bestRec ? bestRec.recognition : null, recKey,
				last ? "pronDialNoBest_tooltip" : "pronDialNone_tooltip")
		}));
		/* No judge for this language on this instance (prod's German, owner 2026-10-19): the
		   Understood dial stands alone — a dial that can never fill reads as broken. */
		if (!state || state.nativeDial !== false) {
			dials.append(buildDial({
				label: res("pronDialAccent_label"), steps: steps,
				last: accentScore(last), best: accentScore(bestAcc),
				lastKey: last ? lastKey : null, bestKey: bestAcc ? accKey : null,
				lastTip: dialTip("pronDialAccLast_tooltip", accentScore(last), lastKey),
				bestTip: dialTip("pronDialAccBest_tooltip", accentScore(bestAcc), accKey,
					last ? "pronDialNoBest_tooltip" : "pronDialNone_tooltip")
			}));
		}

		el.find(".pronLegendClass").text(last ? res("pronDialLegend_label") : "");
		paintDiagnosis(spec, last);
		if (last) {
			el.find(".pronHintClass").removeClass("pronHintWarnClass pronHintNoteClass")
				.css("display", "none");
		}
		position();
	}

	/**
	 * A refusal in place, in one line: what happened and what the constraint is, then stop. No
	 * advice about what to do instead — the learner can see the microphone.
	 */
	function paintRefusal(code, r) {
		var msg;
		if (code === "CAP") {
			msg = res("pronRefusalCap_message");
		} else if (code === "UNAVAILABLE") {
			msg = res("pronRefusalUnavailable_message");
		} else if (code === "AUDIO") {
			msg = res("pronRefusalAudio_message");
		} else if (code === "NO_REFERENCE") {
			msg = res("pronRefusalNoWord_message");
		} else {
			msg = res("pronRefusalProvider_message");
		}
		if (r && r.last) { paintVerdict(current, stateOf(r)); }
		popupEl.find(".pronHintClass").removeClass("pronHintNoteClass")
			.addClass("pronHintWarnClass").text(msg).css("display", "block");
		position();
	}

	// ── the loop ─────────────────────────────────────────────────────────────────────────────

	function playReference() {
		var s = current;
		if (!s) { return; }
		if (typeof s.play === "function") { s.play(); return; }
		if (s.termId != null && window.PlSound) {
			window.PlSound.play(s.termId, s.ipaVariantId || null, null, dialect());
		} else if (s.phraseKind && window.plPlayPhraseSound) {
			window.plPlayPhraseSound(s.phraseKind.toLowerCase() === "collocation"
				? "collocation" : "example", s.phraseRefId, popupEl.find(".pronSayClass")[0]);
		}
	}

	function startRecording() {
		if (recording || busy || !current) { return; }
		if (!window.pronounceRecorder || !pronounceRecorder.isSupported()) {
			showMessage(res("pronNoMic_message"));
			return;
		}
		if (!recorder) { recorder = pronounceRecorder.create(); }   // normally warmed at open
		recording = true;
		popupEl.addClass("pronRecordingClass");
		popupEl.find(".pronHintClass").removeClass("pronHintWarnClass pronHintNoteClass")
			.text(res("pronHint_recording")).css("display", "block");
		document.addEventListener("pointerup", onRelease, true);
		recorder.start().catch(function () {
			// Permission refused, or no device. One sentence, then stop.
			stopRecording(true);
			showMessage(res("pronNoMic_message"));
		});
	}

	function onRelease() { stopRecording(false); }

	function stopRecording(abandon) {
		if (!recording) { return; }
		recording = false;
		document.removeEventListener("pointerup", onRelease, true);
		popupEl.removeClass("pronRecordingClass");
		var rec = recorder;   // stays module-owned: the stream keeps warm across holds
		if (!rec) { return; }
		if (abandon) { rec.cancel(); return; }
		var s = current;
		busy = true;
		popupEl.find(".pronHintClass").removeClass("pronHintWarnClass pronHintNoteClass")
			.text(res("pronHint_scoring")).css("display", "block");
		rec.stop().then(function (clip) {
			if (clip.silent) {
				// Caught in the page, before it costs a call: an empty clip comes back from the
				// provider as "recognised no speech", which reads to a learner as a verdict on
				// their pronunciation rather than on their microphone.
				busy = false;
				popupEl.find(".pronHintClass").addClass("pronHintWarnClass")
					.text(res("pronSilent_message"));
				return;
			}
			// Held until the server names the attempt it belongs to — the dials are keyed by id.
			pendingClip = clip.blob;
			send(s, clip.wav);
		}).catch(function () {
			busy = false;
			popupEl.find(".pronHintClass").addClass("pronHintWarnClass")
				.text(res("pronRefusalAudio_message"));
		});
	}

	function send(s, wav) {
		var form = new FormData();
		form.append("audio", new Blob([wav], { type: "audio/wav" }), "attempt.wav");
		var params = refParams(s);
		for (var k in params) {
			if (params.hasOwnProperty(k) && params[k] != null) { form.append(k, params[k]); }
		}
		$.ajax({
			url: "api/pronounce/assess", type: "POST", data: form,
			processData: false, contentType: false, dataType: "json"
		}).done(function (r) {
			busy = false;
			if (current !== s) { pendingClip = null; return; }
			if (r && r.ok) {
				// The blob is filed BEFORE the dials are drawn, or the ring it belongs to would be
				// painted as having no recording behind it.
				bindAudio(s, r.attempt);
				paintVerdict(s, stateOf(r));
			} else {
				pendingClip = null;   // no verdict, no attempt to hang a recording on
				paintRefusal(r && r.refusal, r);
			}
		}).fail(function () {
			busy = false;
			pendingClip = null;
			if (current !== s) { return; }
			paintRefusal("PROVIDER", null);
		});
	}

	// ── the control every surface plants ─────────────────────────────────────────────────────

	/**
	 * The microphone that stands beside a speaker. Order everywhere is Add · Mic · Sound, which is
	 * the caller's job — this only builds the button.
	 *
	 * @param spec  the reference, or a function returning it (Learn and the drill re-use one strip
	 *              across cards, so the button must ask again at click time rather than remember).
	 * @param cls   extra classes, so a surface can match its own icon sizes.
	 */
	function micButton(spec, cls) {
		return $('<button type="button"></button>')
			.addClass("pl-btn-toggle pronMicClass " + (cls || ""))
			.attr("aria-pressed", "false")
			.append($('<span class="pl-ic pl-ic-mic"></span>'))
			.assignTooltip("pronMic_tooltip")
			.on("click", function (e) {
				e.stopPropagation();
				open(this, spec);
			});
	}

	/** The same button as raw HTML, for the string-built surfaces (the lean row, the gutters). */
	function micButtonHtml(cls) {
		return '<button type="button" class="pl-btn-toggle pronMicClass ' + (cls || '') + '"'
			+ ' aria-pressed="false"><span class="pl-ic pl-ic-mic"></span></button>';
	}

	return {
		open: open,
		close: close,
		isOpen: function () { return !!layer; },
		micButton: micButton,
		micButtonHtml: micButtonHtml,
		/* Exposed for the smoke harness, which cannot hold a real microphone: it injects a
		   server-shaped state and checks what gets drawn. Never called by the app itself. */
		_paintState: function (spec, state) {
			if (!popupEl) { return; }
			current = spec;
			paintVerdict(spec, state);
		},
		/* Also smoke-only: pretend a recording is still held for this attempt id, so the harness
		   can prove the play affordance appears for it and not for the other one. */
		_holdAudio: function (attemptId) {
			myAudio["a" + attemptId] = { url: "", audio: { play: function () {}, currentTime: 0 } };
		}
	};
})();
